/**
 * Field Mapping Component
 * 
 * Advanced field mapping interface for Set node and similar nodes
 * Allows users to select fields from input data and map them to output fields
 */

import { useState, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  ChevronRight, 
  ChevronDown,
  Copy,
  Sparkles
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { Node, Edge, getIncomers } from 'reactflow';
import { useWorkflowStore } from '@/stores/workflow-store';
import { cn } from '@/lib/utils';
import { useDrop } from 'react-dnd';
import { DragItem } from '@/contexts/DragDropContext';

interface FieldMapping {
  name: string;
  value: string;
}

interface FieldMappingFieldProps {
  property: InferredPropertySchema;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  nodeId?: string;
}

// Droppable Input Component using react-dnd
function DroppableInput({
  value,
  onChange,
  placeholder,
  className,
  index,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
  index: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Setup drop target for schema fields
  const [{ isOver, canDrop }, dropRef] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: 'schema-field',
    drop: (item: DragItem) => {
      
      // Convert the path to an expression format
      // item.path is like "output.item.field"
      // We need to convert it to "{{ $json.field }}" format
      let expression = '';
      
      if (item.path) {
        // Remove 'output.' prefix if present
        const pathWithoutOutput = item.path.replace(/^output\./, '');
        
        // Create the expression
        expression = `{{ $json.${pathWithoutOutput} }}`;
      }
      
      onChange(expression);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const isDragOver = isOver && canDrop;

  return (
    <div
      ref={dropRef as any}
      className={cn(
        'relative transition-all',
        isDragOver && 'ring-2 ring-primary ring-offset-2'
      )}
    >
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          className,
          isDragOver && 'border-primary bg-primary/5'
        )}
      />
      {isDragOver && (
        <div className="absolute inset-0 flex items-center justify-center bg-primary/10 rounded-md pointer-events-none">
          <span className="text-xs font-medium text-primary">Drop field here</span>
        </div>
      )}
    </div>
  );
}

export function FieldMappingField({
  property,
  value,
  onChange,
  error,
  nodeId,
}: FieldMappingFieldProps) {
  const { nodes, edges } = useWorkflowStore();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Parse the value structure
  const mappings: FieldMapping[] = useMemo(() => {
    if (!value) return [];
    
    // Handle fixedCollection structure: { values: [{ name: '', value: '' }] }
    if (value.values && Array.isArray(value.values)) {
      return value.values;
    }
    
    // Handle array structure: [{ name: '', value: '' }]
    if (Array.isArray(value)) {
      return value;
    }
    
    return [];
  }, [value]);

  // Get input fields from connected nodes
  const inputFields = useMemo(() => {
    if (!nodeId) return [];
    
    const currentNode = nodes.find(n => n.id === nodeId);
    if (!currentNode) return [];
    
    const sourceNodes = getIncomers(currentNode, nodes, edges);
    const fields: Array<{ path: string; label: string; nodeLabel: string }> = [];
    
    sourceNodes.forEach(node => {
      const nodeLabel = node.data.label || node.type;
      
      // Get execution output if available
      const executionOutput = node.data.executionOutput;
      
      if (executionOutput && Array.isArray(executionOutput) && executionOutput.length > 0) {
        // Get fields from first item
        const firstItem = executionOutput[0];
        if (typeof firstItem === 'object' && firstItem !== null) {
          Object.keys(firstItem).forEach(key => {
            fields.push({
              path: `{{ $node["${nodeLabel}"].${key} }}`,
              label: key,
              nodeLabel: nodeLabel,
            });
          });
        }
      } else {
        // Fallback: suggest common fields
        fields.push({
          path: `{{ $node["${nodeLabel}"] }}`,
          label: 'All data',
          nodeLabel: nodeLabel,
        });
      }
    });
    
    return fields;
  }, [nodeId, nodes, edges]);

  const handleAddMapping = () => {
    const newMapping: FieldMapping = {
      name: '',
      value: '',
    };
    
    const newMappings = [...mappings, newMapping];
    updateValue(newMappings);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mappings.filter((_, i) => i !== index);
    updateValue(newMappings);
  };

  const handleUpdateMapping = (index: number, field: 'name' | 'value', fieldValue: string) => {
    const newMappings = [...mappings];
    newMappings[index] = {
      ...newMappings[index],
      [field]: fieldValue,
    };
    updateValue(newMappings);
  };

  const handleSelectField = (index: number, fieldPath: string, fieldLabel: string) => {
    const newMappings = [...mappings];
    newMappings[index] = {
      name: newMappings[index].name || fieldLabel,
      value: fieldPath,
    };
    updateValue(newMappings);
  };

  const handleQuickAddField = (fieldPath: string, fieldLabel: string) => {
    const newMapping: FieldMapping = {
      name: fieldLabel,
      value: fieldPath,
    };
    
    const newMappings = [...mappings, newMapping];
    updateValue(newMappings);
  };

  const updateValue = (newMappings: FieldMapping[]) => {
    // Maintain the original structure
    if (value && value.values) {
      onChange({ values: newMappings });
    } else {
      onChange(newMappings);
    }
  };

  const toggleNodeExpansion = (nodeLabel: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeLabel)) {
      newExpanded.delete(nodeLabel);
    } else {
      newExpanded.add(nodeLabel);
    }
    setExpandedNodes(newExpanded);
  };

  // Group fields by node
  const fieldsByNode = useMemo(() => {
    const grouped = new Map<string, typeof inputFields>();
    inputFields.forEach(field => {
      if (!grouped.has(field.nodeLabel)) {
        grouped.set(field.nodeLabel, []);
      }
      grouped.get(field.nodeLabel)!.push(field);
    });
    return grouped;
  }, [inputFields]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <div className="flex gap-2">
          {inputFields.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  Quick Add
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <ScrollArea className="h-[300px]">
                  <div className="p-2 space-y-1">
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      Available Fields
                    </div>
                    {Array.from(fieldsByNode.entries()).map(([nodeLabel, fields]) => (
                      <div key={nodeLabel} className="space-y-1">
                        <button
                          onClick={() => toggleNodeExpansion(nodeLabel)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded-md"
                        >
                          {expandedNodes.has(nodeLabel) ? (
                            <ChevronDown className="w-3 h-3" />
                          ) : (
                            <ChevronRight className="w-3 h-3" />
                          )}
                          <span className="font-medium">{nodeLabel}</span>
                          <Badge variant="secondary" className="ml-auto text-[10px]">
                            {fields.length}
                          </Badge>
                        </button>
                        {expandedNodes.has(nodeLabel) && (
                          <div className="ml-5 space-y-0.5">
                            {fields.map((field, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleQuickAddField(field.path, field.label)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded-md text-left"
                              >
                                <Copy className="w-3 h-3 text-muted-foreground" />
                                <span className="flex-1 truncate">{field.label}</span>
                                <Plus className="w-3 h-3 text-muted-foreground" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddMapping}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Field
          </Button>
        </div>
      </div>

      {property.description && (
        <p className="text-xs text-muted-foreground">{property.description}</p>
      )}

      {mappings.length === 0 ? (
        <div className="text-xs text-muted-foreground italic p-4 border border-dashed rounded-md text-center">
          No fields mapped yet. Click "Add Field" or "Quick Add" to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {mappings.map((mapping, index) => (
            <div
              key={index}
              className="p-3 border rounded-md bg-muted/30 space-y-3 relative group"
            >
              {/* Mapping Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    Field {index + 1}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveMapping(index)}
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>

              {/* Field Name */}
              <div className="space-y-1.5">
                <Label className="text-xs">Output Field Name</Label>
                <Input
                  value={mapping.name}
                  onChange={(e) => handleUpdateMapping(index, 'name', e.target.value)}
                  placeholder="fieldName"
                  className="h-8 text-sm"
                />
              </div>

              {/* Field Value */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Value / Expression</Label>
                  {inputFields.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Select Field
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0" align="end">
                        <ScrollArea className="h-[250px]">
                          <div className="p-2 space-y-1">
                            {Array.from(fieldsByNode.entries()).map(([nodeLabel, fields]) => (
                              <div key={nodeLabel} className="space-y-1">
                                <button
                                  onClick={() => toggleNodeExpansion(nodeLabel)}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded-md"
                                >
                                  {expandedNodes.has(nodeLabel) ? (
                                    <ChevronDown className="w-3 h-3" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3" />
                                  )}
                                  <span className="font-medium">{nodeLabel}</span>
                                </button>
                                {expandedNodes.has(nodeLabel) && (
                                  <div className="ml-5 space-y-0.5">
                                    {fields.map((field, idx) => (
                                      <button
                                        key={idx}
                                        onClick={() => handleSelectField(index, field.path, field.label)}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded-md text-left"
                                      >
                                        <span className="flex-1 truncate font-mono text-[11px]">
                                          {field.label}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                <DroppableInput
                  value={mapping.value}
                  onChange={(newValue) => handleUpdateMapping(index, 'value', newValue)}
                  placeholder="={{$json.field}} or static value"
                  className="h-8 text-sm font-mono"
                  index={index}
                />
                <p className="text-[10px] text-muted-foreground">
                  Drag fields from the left panel or use expressions like <code className="px-1 py-0.5 bg-muted rounded">{'{{$json.field}}'}</code>
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
